/**
 * Room booking (YB-2066 / YB-2073).
 *
 * Every operation returns the standard envelope with a **JSON** `data` payload, so there is no
 * field selection to build with `Object.keys(new SomeInput())` here — the shapes are documented on
 * the models in `@app/models/booking.model`.
 *
 * `client_id` is auto-injected into the variables by ApiService, which is why no caller passes it.
 * PAPI needs it on every operation because bookings live in the tenant DB and booking ids collide
 * across tenants.
 */

export const GET_BOOKABLE_AREAS = `
query get_bookable_areas($client_id: Float!, $event_id: Float) {
    get_bookable_areas(client_id: $client_id, event_id: $event_id) {
        success
        errorText
        data
    }
}
`

/**
 * Who may this room be booked for — the Booked-by picker's search.
 *
 * Not `get_passes(search:)`: person columns are encrypted at rest, so that query can only compare
 * ciphertext for equality and matches nothing short of a whole last name or a whole phone number.
 * This one decrypts server-side before matching, so a first name, a fragment, or a phone typed in
 * any spelling all find the person — and it only returns holders create_booking would accept.
 */
export const SEARCH_BOOKING_CANDIDATES = `
query search_booking_candidates($client_id: Float!, $area_id: Float!, $search: String!, $take: Float) {
    search_booking_candidates(client_id: $client_id, area_id: $area_id, search: $search, take: $take) {
        success
        errorText
        data
    }
}
`

export const GET_BOOKINGS = `
query get_bookings($client_id: Float!, $from_date: String!, $to_date: String!, $event_id: Float, $area_ids: [Float!], $pass_id: Float, $status: String) {
    get_bookings(client_id: $client_id, from_date: $from_date, to_date: $to_date, event_id: $event_id, area_ids: $area_ids, pass_id: $pass_id, status: $status) {
        success
        errorText
        data
    }
}
`

export const GET_BOOKING_AVAILABILITY = `
query get_booking_availability($client_id: Float!, $area_ids: [Float!]!, $from_date: String!, $to_date: String!, $pass_id: Float) {
    get_booking_availability(client_id: $client_id, area_ids: $area_ids, from_date: $from_date, to_date: $to_date, pass_id: $pass_id) {
        success
        errorText
        data
    }
}
`

export const GET_BOOKING_USAGE_REPORT = `
query get_booking_usage_report($client_id: Float!, $group_by: String!, $from_date: String!, $to_date: String!, $event_id: Float) {
    get_booking_usage_report(client_id: $client_id, group_by: $group_by, from_date: $from_date, to_date: $to_date, event_id: $event_id) {
        success
        errorText
        data
    }
}
`

export const CREATE_BOOKING = `
mutation create_booking($input: CreateBookingInput!) {
    create_booking(input: $input) {
        success
        errorText
        data
    }
}
`

export const UPDATE_BOOKING = `
mutation update_booking($client_id: Float!, $booking_id: Float!, $input: UpdateBookingInput!) {
    update_booking(client_id: $client_id, booking_id: $booking_id, input: $input) {
        success
        errorText
        data
    }
}
`

export const CANCEL_BOOKING = `
mutation cancel_booking($client_id: Float!, $booking_id: Float!, $reason: String) {
    cancel_booking(client_id: $client_id, booking_id: $booking_id, reason: $reason) {
        success
        errorText
        data
    }
}
`

export const END_BOOKING = `
mutation end_booking($client_id: Float!, $booking_id: Float!) {
    end_booking(client_id: $client_id, booking_id: $booking_id) {
        success
        errorText
        data
    }
}
`
